import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@/App';
import {
  commonApi,
  getShopifyUserId,
  getQueryVariable,
  customStyles,
  debounce,
  noOpenModal,
  deleteDOM,
  pollCookie,
  companyUUID,
} from '@/service/api';
import { terminal } from '@/constant/index';
import { showBanner } from '@/tools/tanChuang/showBanner';
import { LogJsError } from '@/service/monitor';
import {
  cartseeJSNames,
  computeJSStart,
  getJsFileRequestTime,
} from '@/utils/timing-calc';
import { getPopupConfig } from '@/service/strategy/logic';
import { showIcon } from '@/service/strategy/iconLogic';
import { executeWhenReady } from '@/utils/execute';
import { monitorGA } from '@/utils/gaMonitor';
import { isProductPage } from '@/utils/isProductPage';
import { chatMonitor } from '@/utils/chatMonitor';
import { checkoutMonitor } from '@/utils/checkoutMonitor';
import { init468 } from '@/utils/init468';
import { common, shopify, shopline, shoplazza } from '@/constant/company';
import { loadScript } from '@/utils/loadScript';
import { oneidInit } from '@/utils/oneId';
import {
  AOOLIA,
  DRAW,
  EVENTS,
  STORAGE_KEY,
  HEYPROM,
  MISSACC,
  SMALLRIG,
  HELLOICE,
} from '@/constant/constant';
import {
  renderSoldOutButton,
  getProduct as shopifyGetProduct,
  getCurrentVariant as shopifyGetCurrentVariant,
} from '@/utils/shopify';
import { isInShoplazzaEditor } from '@/utils/shoplazza';
import { initEvents, PATH_CHANGE_EVENT } from '@/utils/events';
import { isBlockSpider, isEnableNewsletterMonitor } from '@/utils/experiment';
import { initPlatformAddCart } from '@/utils/addCart/platformAdapter';
import EmbeddedPopup from '@/components/EmbeddedPopup';
import EmbeddedPopupNew from '@/components/EmbeddedPopup/indexNew';
import { checkConfigFrom } from '@/service/strategy/dataStorage';
import { clearDom } from '@/utils/dom';
import { clearAll } from '@/service/strategy/show';
import { formEventV2 } from '@/service/formEventV2';
import { initCartsee } from '@/utils/cartsee';
import { shopifyNewsletter } from '@/utils/shopify/newsletter';
import { shoplineNewsletter } from '@/utils/shopline/newsletter';
import { getCompanyStaticUrl } from '@/utils';
import {
  getLocal,
  getSession,
  removeSession,
  setLocal,
  setSession,
  removeLocal,
} from '@/utils/storage';
import { safeJsonParse } from '@/utils/safeJsonParse';
import {
  isShopifyOrTest,
  isShopyyOrTest,
  isShoplazzaOrTest,
  isShoplineOrTest,
  isXikeOrTest,
  isCommonOrTest,
  isSimpleOrTest,
  isSaaSPlatform,
} from '@/utils/platform';
import { popupFlowLog } from '@/utils/popupFlowDebug';

const debug = require('debug')('cartsee:main');
console.info('[cartx-override-check] local sdk bundle loaded');

try {
  // 爬虫彻底零请求早退：不打 spider_user_active / load / view / user_active，不跑业务初始化
  if (isBlockSpider()) {
    debug('命中爬虫 companyId=%s url=%s', companyUUID, window.location.href);
  } else {
    let disabled = false;
    // shopyy 加购iframe里不执行，防止显示icon
    if (isShopyyOrTest()) {
      try {
        if (window.self !== window.top) {
          disabled = true;
          debug('当前页面在 iframe 中');
        } else {
          debug('当前页面不在 iframe 中');
        }
      } catch (e) {
        disabled = true;
        console.error('无法访问 window.top，可能是跨域的 iframe');
      }
    }

    if (isShoplazzaOrTest()) {
      if (isInShoplazzaEditor()) {
        disabled = true;
        debug('当前页面在 shoplazza 编辑器中');
      } else {
        debug('当前页面不在 shoplazza 编辑器中');
      }
    }

    // 初始化事件
    try {
      initEvents();
    } catch (e) {
      console.error('initEvents error:', e);
    }

    const main = () => {
      if (!window.cartxPopAPI && !disabled && companyUUID !== 'lu279wve') {
        debug('cartsee init');
        // 显示控制：加载js时，清除显示弹窗
        // 显示控制：用于控制在icon点击显示弹窗时，网络请求的弹窗下发拦截
        removeSession(STORAGE_KEY.POP_SHOW);
        // 641，清除悬浮窗缓存
        if ([shopline[641], shopline[2244]].includes(companyUUID)) {
          removeLocal(STORAGE_KEY.TEMP_POP);
        }

        /** 33强制清除code */
        if (isShopifyOrTest() && [shopify[33]].includes(companyUUID)) {
          removeLocal(STORAGE_KEY.CARTX_FLOAT_BANNER);
          removeLocal(STORAGE_KEY.CARTX_FLOAT_BANNER_DATA);
        }

        window.cartxPopAPI = true;

        // 加载oneid
        try {
          // 灰度店铺
          oneidInit();
        } catch (e) {
          console.error('oneidInit error:', e);
        }

        if (
          window.location.href.includes('vconsole=1') ||
          getLocal(STORAGE_KEY.VCONSOLE) === '1'
        ) {
          loadScript({
            url: 'https://unpkg.com/vconsole@3.15.1/dist/vconsole.min.js',
            async: true, // 设置为true以异步加载脚本
            defer: false, // 设置为false以立即执行脚本（不考虑async），或等待文档解析完成（如果async也为true）
          })
            .then(() => {
              debug('Script loaded successfully!');
              window.VConsole && new window.VConsole();
              setLocal(STORAGE_KEY.VCONSOLE, '1');
            })
            .catch((error) => {
              console.error('Script loading failed:', error.message);
            });
        }

        // 采集用户状态
        let state = {};
        try {
          const config = safeJsonParse<any>(
            getSession(STORAGE_KEY.POPUP),
            {} as any,
          );
          const { abMapping, formGlobalSetting, userStatus } = config;
          state = {
            stat: getLocal(STORAGE_KEY.CARTSEE_STAT),
            allPopCount: getSession(STORAGE_KEY.ALL_POP_COUNT),
            leavePopCount: getSession(STORAGE_KEY.LEAVE_POP_COUNT),
            abMapping,
            formGlobalSetting,
            userStatus,
            popupCount: config?.rets?.length,
          };
        } catch (e) {
          LogJsError(e);
        }

        // 用户到站埋点
        formEventV2({
          event: 'user_active',
          url: window.location.href,
          userAgent: window.navigator.userAgent,
          // 上报用户状态
          result: JSON.stringify(state),
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});

        if (isXikeOrTest() || isShoplineOrTest() || isShopifyOrTest()) {
          try {
            monitorGA();
          } catch (e) {
            console.error(e);
          }
        }

        const pview = {
          p1: false,
          p2: false,
          p3: false,
          p4: false,
          p5: false,
          p6: false,
          scroll400: false,
        };
        // 用户浏览的埋点监听
        const userView = () => {
          var scrollTop =
            window.pageYOffset ||
            document.documentElement.scrollTop ||
            document.body.scrollTop;
          var innerHeight = window.innerHeight;
          var scrollHeight = document.body.scrollHeight;

          // 317加stream对比埋点
          if (
            [
              shopify[145],
              shopline[317],
              shoplazza[306],
              shoplazza[671],
              shopify[328],
            ].includes(companyUUID)
          ) {
            if (scrollTop > 0 && scrollTop >= 400 && !pview.scroll400) {
              pview.scroll400 = true;
              formEventV2({
                event: 'user_view',
                pixel: '400',
                percentage: parseInt(
                  '' + ((scrollTop + innerHeight) / scrollHeight) * 100,
                ),
              })
                .then((response) => {
                  // console.log('数据上报成功')
                })
                .catch((e) => {});
            }
          }

          if (scrollTop > 0 && scrollTop <= 1000 && !pview.p1) {
            pview.p1 = true;
            formEventV2({
              event: 'user_view',
              pixel: '0-1000',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          } else if (scrollTop > 1000 && scrollTop <= 2000 && !pview.p2) {
            pview.p2 = true;
            formEventV2({
              event: 'user_view',
              pixel: '1000-2000',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          } else if (scrollTop > 2000 && scrollTop <= 3000 && !pview.p3) {
            pview.p3 = true;
            formEventV2({
              event: 'user_view',
              pixel: '2000-3000',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          } else if (scrollTop > 3000 && scrollTop < 4000 && !pview.p4) {
            pview.p4 = true;
            formEventV2({
              event: 'user_view',
              pixel: '3000-4000',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          } else if (scrollTop > 4000 && scrollTop < 5000 && !pview.p5) {
            pview.p5 = true;
            formEventV2({
              event: 'user_view',
              pixel: '4000-5000',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          } else if (scrollTop > 5000 && !pview.p6) {
            pview.p6 = true;
            formEventV2({
              event: 'user_view',
              pixel: '5000+',
              percentage: parseInt(
                (((scrollTop + innerHeight) / scrollHeight) * 100).toString(),
              ),
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          }
        };
        window.addEventListener('scroll', userView);

        // 计算js脚本文件被浏览器拉取是从什么时候开始的，在浏览器页面加载完后的xxxx毫秒
        formEventV2({
          event: 'js_start',
          requestStart: `${getJsFileRequestTime(cartseeJSNames())}`,
          requestStart1: `${getJsFileRequestTime(`${'stream'}${'-'}${'subscribe'}${'.'}${'js'}`)}`,
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});

        // 317加stream对比埋点
        if (
          [
            shopify[145],
            shopline[317],
            shoplazza[306],
            shoplazza[671],
            shopify[328],
          ].includes(companyUUID)
        ) {
          computeJSStart({
            event: 'stream_js_start',
          });
        }

        // js加载埋点监听
        formEventV2({
          event: 'js_load',
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});

        // 弹窗请求埋点监听

        if (getQueryVariable('cid')) {
          setLocal(STORAGE_KEY.CID, getQueryVariable('cid'));
        }

        // console.log("🚀 ~ file: index.js:20 ~ terminal", terminal)

        let load = function (
          actionType: number,
          url: string,
          externalRes?: any,
          coupon?: string,
          options?: any,
        ) {
          //接口请求发起时间
          let loadStartTime = new Date().getTime();
          (window as any).cartseeLoad = load;
          popupFlowLog(
            'load start actionType=%s url=%s trigger=%s pathname=%s',
            actionType,
            url,
            options?.trigger ?? '',
            window.location.pathname,
          );

          let param = {
            cid: getLocal(STORAGE_KEY.CID) || null,
            formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
            pathname: window.location.pathname,
            hostname: window.location.hostname,
            parameters: window.location.search,
            event: 'load',
            companyUUID: companyUUID,
            shopifyUserId: getShopifyUserId(),
            loginEmail: window?.cartq?.params?.customer?.email || '',
            userAgent: navigator.userAgent,
            terminal: terminal ? 'pc' : 'mobile',
            triggerEventType: actionType, // 0 欢迎订阅弹框  1 离开订阅弹框  2 加购订阅弹框  3 优惠券弹框  4 自定义订阅弹框  5 弃单弹窗  6 缺货关注
          };

          //优惠券弹框新增参数
          if (coupon) {
            param['templateMessageId'] = coupon;
          }

          if (actionType === 2 && externalRes) {
            setSession(
              STORAGE_KEY.CART_LAST_IMAGE,
              JSON.stringify(externalRes),
            );
          }

          function resSuccess(response) {
            // console.log(actionType);
            // const isShopify = process.env.REACT_APP_DEPLOY_ENV === 'shopify' || process.env.REACT_APP_DEPLOY_ENV === 'shopifyTest';
            // console.log('是否是shopify', isShopify);
            // const hasAddCartFlag = sessionStorage?.getItem('_cartsee_state') === '1';
            // console.log('有加购标记：', hasAddCartFlag);
            // const isCartPage = window?.location?.pathname?.indexOf('/cart') !== -1;
            // console.log('是否是加购页', isCartPage);
            // shopify的加购，需要在购物车页，或者有加购标记
            if (actionType === 2 && isSaaSPlatform()) {
              const addCartState =
                getSession(STORAGE_KEY.ADD_CART_STATE) === '1';
              const addCartJump = getSession(STORAGE_KEY.ADD_CART_JUMP) === '1';
              const isCartPath =
                window?.location?.pathname?.indexOf('/cart') !== -1;
              if (addCartState || addCartJump || isCartPath) {
                // 下一步
              } else {
                popupFlowLog(
                  'resSuccess blocked actionType=2 load-guard addCartState=%s addCartJump=%s isCartPath=%s pathname=%s',
                  addCartState,
                  addCartJump,
                  isCartPath,
                  window?.location?.pathname,
                );
                return;
              }
            }

            // @TODO 先简单实现多场景优先级避让，后面优化
            // 同场景优先级避让
            let div = document.createElement('div');
            div.className = 'cartx-mount-wrap';
            if (actionType == 0 || actionType == 4 || actionType === 8) {
              // 存在已渲染的弹窗，则不展示
              const dom = document.getElementById('cartx-toleave-wrap');
              if (dom && !noOpenModal()) {
                popupFlowLog(
                  'resSuccess blocked actionType=%s reason=leave_dom_present cartx-toleave-wrap noOpenModal=%s',
                  actionType,
                  noOpenModal(),
                );
                return;
              }

              //删除上次的dom
              deleteDOM('cartx-wrap');
              //欢迎订阅 自定义订阅
              div.id = 'cartx-wrap';
              customStyles(response, '#cartx-wrap');
            }
            if (actionType == 1 || actionType == 5) {
              // 存在已渲染的弹窗，则不展示
              const dom = document.getElementById('cartx-wrap');
              if (dom && !noOpenModal()) {
                popupFlowLog(
                  'resSuccess blocked actionType=%s reason=welcome_dom_present cartx-wrap noOpenModal=%s',
                  actionType,
                  noOpenModal(),
                );
                return;
              }

              //删除上次的dom
              deleteDOM('cartx-toleave-wrap');
              //离开订阅/弃单弹窗
              div.id = 'cartx-toleave-wrap';
              customStyles(response, '#cartx-toleave-wrap');
            }
            if (actionType == 2) {
              //删除上次的dom
              deleteDOM('cartx-purchase-wrap');
              //加购订阅
              div.id = 'cartx-purchase-wrap';
              customStyles(response, '#cartx-purchase-wrap');
            }
            if (actionType == 3) {
              //删除上次的dom
              deleteDOM('cartx-coupon-wrap');
              //加购订阅
              div.id = 'cartx-coupon-wrap';
              customStyles(response, '#cartx-coupon-wrap');
            }
            if (actionType == 6) {
              //删除上次的dom
              deleteDOM('cartx-popup-wrap');
              //加购订阅
              div.id = 'cartx-popup-wrap';
              customStyles(response, '#cartx-popup-wrap');
            }
            window?.document?.body?.appendChild(div);

            let rootNode;
            if (actionType == 0 || actionType == 4 || actionType == 8) {
              //欢迎订阅 自定义订阅
              rootNode = document.getElementById('cartx-wrap');
            }
            if (actionType == 1 || actionType == 5) {
              //离开订阅/弃单弹窗
              rootNode = document.getElementById('cartx-toleave-wrap');
            }
            if (actionType == 2) {
              //加购订阅
              rootNode = document.getElementById('cartx-purchase-wrap');
            }
            if (actionType == 3) {
              //优惠券弹框
              rootNode = document.getElementById('cartx-coupon-wrap');
            }
            if (actionType == 6) {
              // 缺货弹框
              rootNode = document.getElementById('cartx-popup-wrap');
            }
            // console.log(actionType);
            const root = ReactDOM.createRoot(rootNode);

            //接口请求结束时间
            let loadEndTime = new Date().getTime();
            root.render(
              <App
                settings={response.data.data?.settings ?? {}}
                type={response.data.data?.type ?? ''}
                expand={response.data.data?.expand ?? false}
                id={response.data.data?.id ?? ''}
                combinationType={response.data.data?.combinationType ?? ''}
                triggerEventType={response.data.data?.triggerEventType ?? ''}
                responseData={response.data.data}
                externalRes={externalRes ?? ''}
                upgradeLoad={upgradeLoad}
                extraTime={loadEndTime - loadStartTime}
                actionType={actionType}
                triggerSource={options?.trigger}
                name={response.data.data?.name ?? ''}
                loadStartTime={loadStartTime}
              />,
            );
          }
          if (actionType === 1) {
            const reg = /\/checkout(s)?/i;
            // 属于弃单的埋点
            if (reg.test(window.location.pathname)) {
              // 弹窗请求埋点监听
              formEventV2({
                event: 'popup_request',
                triggerEventType: 5,
              })
                .then((response) => {
                  // console.log('数据上报成功')
                })
                .catch((e) => {});
            } else {
              formEventV2({
                event: 'popup_request',
                triggerEventType: 1,
              })
                .then((response) => {
                  // console.log('数据上报成功')
                })
                .catch((e) => {});
            }
          } else {
            // 弹窗请求埋点监听
            formEventV2({
              event: 'popup_request',
              triggerEventType: actionType,
            })
              .then((response) => {
                // console.log('数据上报成功')
              })
              .catch((e) => {});
          }

          // 全量所有渠道就是开关直接打开，不再加判断
          if (true) {
            // console.log('请求配置类型', actionType);
            // console.log('请求配置或检查配置开始:', new Date().toLocaleString());
            // console.log('start request', actionType);
            const getResponse = (res) => {
              popupFlowLog(
                'load getResponse -> resSuccess actionType=%s formId=%s formName=%s triggerEventType=%s',
                actionType,
                res?.id,
                res?.name,
                res?.triggerEventType,
              );
              // 埋点，popup_load_success
              formEventV2({
                event: 'popup_load_success',
                triggerEventType: res?.triggerEventType,
                formId: res?.id,
                formName: res?.name,
                pixel:
                  window.pageYOffset ||
                  document.documentElement.scrollTop ||
                  document.body.scrollTop,
                // 弹窗完全加载完成 - load请求发起的时间
                loadTime: new Date().getTime() - loadStartTime,
              })
                .then((response) => {
                  // console.log('数据上报成功')
                })
                .catch((e) => {});

              // console.log('res', res, actionType);
              if (window?.document?.body) {
                // 按照以前的结构组装
                resSuccess({ data: { data: res } });
              } else {
                const docCallback = () => {
                  if (
                    window?.document?.readyState === 'interactive' ||
                    window?.document?.readyState === 'complete'
                  ) {
                    window?.document?.removeEventListener(
                      'readystatechange',
                      docCallback,
                    );
                    window?.document?.removeEventListener(
                      'DOMContentLoaded',
                      docCallback,
                    );
                    resSuccess({ data: { data: res } });
                  }
                };
                window?.document?.addEventListener(
                  'readystatechange',
                  docCallback,
                );
                window?.document?.addEventListener(
                  'DOMContentLoaded',
                  docCallback,
                );
              }
            };
            getPopupConfig(actionType, loadStartTime, getResponse, options)
              .then(getResponse)
              .catch((err) => {
                popupFlowLog(
                  'load getPopupConfig rejected actionType=%s trigger=%s message=%s',
                  actionType,
                  options?.trigger ?? '',
                  err?.message ?? err,
                );
                // leaveBeta 下，type=0 若因静默/频控/策略不满足导致失败，则同次降级尝试 type=1（避免一直重复 type=0）
                if (
                  actionType === 0 &&
                  options?.trigger === 'leaveBeta' &&
                  options?.fromType0Fallback !== true &&
                  options?.allowFallbackToType1 !== false
                ) {
                  // TODO: 上线前移除调试日志
                  popupFlowLog(
                    '[cartx:leave-trigger] fallback type=0->1 due to getPopupConfig reject',
                  );
                  load(1, 'form/out/leaveLoad', '', '', {
                    trigger: 'leaveBeta',
                    fromType0Fallback: true,
                    allowFallbackToType1: false,
                  });
                }
              });
          }
        };

        // 初始化cartsee自定义弹窗触发
        initCartsee(load);

        //多折扣码配置第二步
        let upgradeLoad = function (id) {
          commonApi(
            {
              cid: getLocal(STORAGE_KEY.CID) || null,
              formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
              pathname: window.location.pathname,
              hostname: window.location.hostname,
              parameters: window.location.search,
              event: 'load',
              companyUUID: companyUUID,
              shopifyUserId: getShopifyUserId(),
              loginEmail: window?.cartq?.params?.customer?.email || '',
              userAgent: navigator.userAgent,
              terminal: terminal ? 'pc' : 'mobile',
              correlationId: id,
            },
            'form/upgrade/load',
          )
            .then((response) => {
              setLocal(STORAGE_KEY.CORRELATION_ID, id);

              customStyles(response, '#cartx-wrap');

              let rootNode = document.getElementById('cartx-wrap');
              if (!rootNode) {
                let div = document.createElement('div');
                div.id = 'cartx-wrap';
                div.className = 'cartx-mount-wrap';
                window?.document?.body?.appendChild(div);
                rootNode = document.getElementById('cartx-wrap');
              }
              const root = ReactDOM.createRoot(rootNode);
              root.render(
                <App
                  settings={response.data.data?.settings ?? {}}
                  type={response.data.data?.type ?? ''}
                  expand={response.data.data?.expand ?? false}
                  id={response.data.data?.id ?? ''}
                  combinationType={response.data.data?.combinationType ?? ''}
                  triggerEventType={response.data.data?.triggerEventType ?? ''}
                  responseData={response.data.data}
                />,
              );
            })
            .catch((e) => {});
        };

        // 给跨模块（如 logic.ts）提供 upgradeLoad，用于点击 icon 后继续二次弹框链路
        (window as any).__cartsee_upgradeLoad = upgradeLoad;

        pollCookie(function () {
          const correlationId = getLocal(STORAGE_KEY.CORRELATION_ID);
          if (correlationId) {
            //多折扣码配置第二步
            upgradeLoad(correlationId);
          } else {
            if (getLocal(STORAGE_KEY.CID) || getLocal(STORAGE_KEY.FORM_UID)) {
              //自定义订阅弹框
              load(4, 'form/out/subscribeLoad');
            } else {
              //欢迎订阅弹框
              load(0, 'form/out/subscribeLoad');
            }
          }
        });

        if (isCommonOrTest()) {
          if ([...HELLOICE].includes(companyUUID)) {
            window.addEventListener('cartx/page/change', () => {
              pollCookie(function () {
                const correlationId = getLocal(STORAGE_KEY.CORRELATION_ID);
                if (correlationId) {
                  //多折扣码配置第二步
                  upgradeLoad(correlationId);
                } else {
                  if (
                    getLocal(STORAGE_KEY.CID) ||
                    getLocal(STORAGE_KEY.FORM_UID)
                  ) {
                    //自定义订阅弹框
                    load(4, 'form/out/subscribeLoad');
                  } else {
                    //欢迎订阅弹框
                    load(0, 'form/out/subscribeLoad');
                  }
                }
              });
            });
          }
        }

        let getLeaveTriggerConfig = function () {
          const popupConfig = safeJsonParse(getSession(STORAGE_KEY.POPUP), {
            rets: [],
          });
          const retsData = popupConfig.rets;
          let leaveBateType1 = false;
          let leaveBateType0 = false;
          let rawLeaveBateType0 = false;
          let leaveBateType0DisplayFrequency = 0;
          retsData &&
            retsData.forEach((popItem) => {
              if (popItem?.settings?.rules?.readyToLeaveBate) {
                if (popItem.triggerEventType === 1) {
                  leaveBateType1 = true;
                }
                if (popItem.triggerEventType === 0) {
                  rawLeaveBateType0 = true;
                  const displayFrequency = Number(
                    popItem?.settings?.rules?.displayFrequency || 0,
                  );
                  if (displayFrequency > leaveBateType0DisplayFrequency) {
                    leaveBateType0DisplayFrequency = displayFrequency;
                  }
                  leaveBateType0 = true;
                }
              }
            });
          return {
            leaveBateType1,
            leaveBateType0,
            rawLeaveBateType0,
            leaveBateType0DisplayFrequency,
          };
        };

        let triggerLeaveLoad = function () {
          const {
            leaveBateType1,
            leaveBateType0,
            rawLeaveBateType0,
            leaveBateType0DisplayFrequency,
          } = getLeaveTriggerConfig();
          const leaveBetaType0LastShownAt = Number(
            getSession(STORAGE_KEY.LEAVE_BETA_TYPE0_LAST_SHOWN_AT) || 0,
          );
          const leaveBetaType0CooldownMs =
            Math.max(0, leaveBateType0DisplayFrequency) * 1000;
          const leaveBetaType0CooldownPassed =
            !leaveBetaType0LastShownAt ||
            Date.now() - leaveBetaType0LastShownAt >= leaveBetaType0CooldownMs;
          popupFlowLog(
            'triggerLeaveLoad leaveBateType1=%s leaveBateType0=%s rawLeaveBateType0=%s leaveBateType0DisplayFrequency=%s leaveBetaType0LastShownAt=%s leaveBetaType0CooldownPassed=%s pathname=%s',
            leaveBateType1,
            leaveBateType0,
            rawLeaveBateType0,
            leaveBateType0DisplayFrequency,
            leaveBetaType0LastShownAt,
            leaveBetaType0CooldownPassed,
            window?.location?.pathname,
          );
          if (leaveBateType0 && leaveBetaType0CooldownPassed) {
            popupFlowLog('load type=0 from leaveBeta');
            load(0, 'form/out/subscribeLoad', '', '', {
              trigger: 'leaveBeta',
              allowFallbackToType1: true,
            });
            return true;
          } else if (leaveBateType1) {
            popupFlowLog('load type=1 from leaveBeta');
            load(1, 'form/out/leaveLoad', '', '', { trigger: 'leaveBeta' });
            return true;
          }
          popupFlowLog('blocked: no leave beta config matched');
          return false;
        };

        let getLoadStatus = function () {
          const popupConfig = safeJsonParse(getSession(STORAGE_KEY.POPUP), {
            rets: [],
          });
          const retsData = popupConfig.rets;
          let status;
          retsData &&
            retsData.forEach((popItem) => {
              if (popItem.triggerEventType === 1) {
                status = true;
              }
            });
          return status;
        };

        //离开订阅弹框 弃单订阅弹框
        if (terminal) {
          //pc
          if (/firefox/i.test(navigator.userAgent)) {
            const element = document.getElementsByTagName('body')[0];
            element.addEventListener('mouseenter', () => {
              //   console.log("mouseenter");
            });
            element.addEventListener('mouseleave', (e) => {
              const { leaveBateType1, leaveBateType0 } =
                getLeaveTriggerConfig();
              const canOpen = noOpenModal();
              popupFlowLog(
                'pc mouseleave(firefox) clientY=%s leaveBateType1=%s leaveBateType0=%s canOpen=%s',
                e?.clientY,
                leaveBateType1,
                leaveBateType0,
                canOpen,
              );
              if (e.clientY > -20 && (leaveBateType1 || leaveBateType0)) {
                popupFlowLog(
                  'blocked top-threshold clientY=%s (need clientY<=-20 for leaveBeta)',
                  e?.clientY,
                );
                return false;
              }
              if (canOpen) {
                if (leaveBateType1 || leaveBateType0) {
                  setSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE, 'PC_navBar_Bate');
                  popupFlowLog('trigger load from firefox mouseleave');
                  triggerLeaveLoad();
                } else {
                  setSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE, 'PC_navBar_Old');
                  popupFlowLog(
                    'trigger load type=1 legacy leave (no leaveBeta flags)',
                  );
                  load(1, 'form/out/leaveLoad');
                }
              } else {
                popupFlowLog('blocked noOpenModal=false');
              }
            });
          } else {
            window?.document?.addEventListener(
              'mouseleave',
              debounce(
                function (e) {
                  const { leaveBateType1, leaveBateType0 } =
                    getLeaveTriggerConfig();
                  const canOpen = noOpenModal();
                  popupFlowLog(
                    'pc mouseleave(debounce) clientY=%s leaveBateType1=%s leaveBateType0=%s canOpen=%s',
                    e?.clientY,
                    leaveBateType1,
                    leaveBateType0,
                    canOpen,
                  );
                  if (e.clientY > -20 && (leaveBateType1 || leaveBateType0)) {
                    popupFlowLog(
                      'blocked top-threshold clientY=%s (need clientY<=-20 for leaveBeta)',
                      e?.clientY,
                    );
                    return false;
                  }
                  if (canOpen) {
                    if (leaveBateType1 || leaveBateType0) {
                      setSession(
                        STORAGE_KEY.LEAVE_TRIGGER_TYPE,
                        'PC_navBar_Bate',
                      );
                      popupFlowLog('trigger load from debounced mouseleave');
                      triggerLeaveLoad();
                    } else {
                      setSession(
                        STORAGE_KEY.LEAVE_TRIGGER_TYPE,
                        'PC_navBar_Old',
                      );
                      popupFlowLog(
                        'trigger load type=1 legacy leave (no leaveBeta flags)',
                      );
                      load(1, 'form/out/leaveLoad');
                    }
                  } else {
                    popupFlowLog('blocked noOpenModal=false');
                  }
                },
                0,
                true,
              ),
            );
          }
        } else {
          //h5
          window.onpopstate = function (event) {
            const { leaveBateType1, leaveBateType0 } = getLeaveTriggerConfig();
            popupFlowLog(
              'h5 onpopstate leaveBateType1=%s leaveBateType0=%s href=%s',
              leaveBateType1,
              leaveBateType0,
              window?.location?.href,
            );
            if (leaveBateType1 || leaveBateType0) {
              setSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE, 'H5_backUrl');
              popupFlowLog('h5 onpopstate -> triggerLeaveLoad');
              triggerLeaveLoad();
            }
          };
          const pushHistory = () => {
            var state = {
              title: 'init',
              url: window.location.pathname,
            };
            window.history.pushState(state, 'init', '#1');
          };
          if (
            getLeaveTriggerConfig().leaveBateType1 ||
            getLeaveTriggerConfig().leaveBateType0
          ) {
            if (
              ![
                shopify[994],
                ...MISSACC,
                ...HELLOICE,
                ...AOOLIA,
                ...DRAW,
                ...HEYPROM,
                ...SMALLRIG,
                'mknrwsqi',
              ].includes(companyUUID)
            ) {
              pushHistory();
            }
          }

          //切换可视窗口
          window?.document?.addEventListener('visibilitychange', () => {
            if (document.visibilityState == 'hidden') {
              if (
                noOpenModal() &&
                !(
                  getLeaveTriggerConfig().leaveBateType1 ||
                  getLeaveTriggerConfig().leaveBateType0
                ) &&
                getLoadStatus()
              ) {
                setSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE, 'H5_tabChange');
                load(1, 'form/out/leaveLoad');
              }
            }
          });
          window.addEventListener('pageshow', (event) => {
            debug('pageshow');
            if (
              [
                shopify[994],
                ...MISSACC,
                ...HELLOICE,
                ...AOOLIA,
                ...DRAW,
                ...HEYPROM,
                ...SMALLRIG,
                'mknrwsqi',
              ].includes(companyUUID) &&
              (getLeaveTriggerConfig().leaveBateType1 ||
                getLeaveTriggerConfig().leaveBateType0)
            ) {
              debug('执行1');
              if (
                event.persisted ||
                (window.performance && window.performance.navigation.type === 2)
              ) {
                // 页面后退或者前进时操作
                debug('执行2');
                popupFlowLog(
                  'h5 pageshow bfcache_or_nav_type_2 -> triggerLeaveLoad',
                );
                triggerLeaveLoad();
              }
              debug('执行3');
              setSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE, 'H5_backUrl');
            }
          });

          //点击浏览器回退按钮(弃单挽留)
          if (
            window.location.href.includes('checkout') &&
            !getSession(STORAGE_KEY.GIVE_UP_ORDER)
          ) {
            load(5, 'form/out/leaveLoad');
          }
        }

        //优惠券弹框
        const coupon = getQueryVariable('coupon');
        if (coupon) {
          pollCookie(function () {
            load(3, 'form/out/couponLoad', '', `${coupon}`.split('#')[0]);
          });
        }

        // 缺货订阅 灰度30, 907
        if (isShopifyOrTest()) {
          const initSoldOutForm = () => {
            const popupConfig = JSON.parse(
              getSession(STORAGE_KEY.POPUP) || '{"rets": []}',
            );
            const soldOutPopupConfig = popupConfig.rets?.find(
              (item) => item.triggerEventType === 6,
            );

            if (!soldOutPopupConfig) {
              debug('未配置缺货弹窗');
              return;
            }

            // 点击触发
            document.body.addEventListener('click', function (event: any) {
              if (event.target.classList.contains('cartx-popup-trigger')) {
                event.preventDefault();
                const variant = shopifyGetCurrentVariant();
                load(
                  6,
                  'form/out/load',
                  variant ? { variant } : {},
                  undefined,
                  { trigger: 'click' },
                );
              }
            });

            // 适配展示按钮，展示上一次触发的弹窗
            if (isProductPage() && soldOutPopupConfig) {
              let addToCartBtn;
              if (['mcldp6ch'].includes(companyUUID)) {
                addToCartBtn = document.querySelector(
                  'form[action*="/cart/add"] [type="submit"].btn--secondary',
                );
              } else {
                addToCartBtn = document.querySelector(
                  'form[action*="/cart/add"] [type="submit"]',
                );
              }
              if (!addToCartBtn) {
                debug('addToCartBtn not found');
                return;
              }
              const form = addToCartBtn?.closest('form[action*="/cart/add"]');
              const variantElem = form?.querySelector(
                '[name="id"]',
              ) as HTMLSelectElement | null;

              const initSoldOutButton = () => {
                shopifyGetProduct((product, error) => {
                  if (error) {
                    debug('获取商品信息失败: %o', error);
                    return;
                  }

                  const variantId =
                    getQueryVariable('variant') || variantElem?.value;
                  let variant;
                  if (!variantId) {
                    variant = product?.variants?.[0];
                  } else {
                    variant = product?.variants?.find(
                      (item) => `${item.id}` === variantId,
                    );
                  }

                  debug(
                    '获取商品信息 variantId=%s, variant=%o',
                    variantId,
                    variant,
                  );
                  renderSoldOutButton(variant, form);
                });
              };
              initSoldOutButton();
              const debouncedInitSoldOutButton = debounce(
                initSoldOutButton,
                100,
              );

              // sku切换
              variantElem.addEventListener(
                'change',
                debouncedInitSoldOutButton,
              );
              document.addEventListener(
                'variant:changed',
                debouncedInitSoldOutButton,
              );
            }
          };

          executeWhenReady(() => {
            const popupConfig = getSession(STORAGE_KEY.POPUP);
            if (!popupConfig) {
              window.addEventListener(EVENTS.POPUP.STORED, initSoldOutForm);
            } else {
              initSoldOutForm();
            }
          });
        }

        // 加购跳转页面展现
        const addCartJumpFlag = getSession(STORAGE_KEY.ADD_CART_JUMP);
        if (addCartJumpFlag === '1') {
          const externalRes = getSession(STORAGE_KEY.CART_LAST_IMAGE);
          if (externalRes) {
            load(
              2,
              'form/out/addPurchaseLoad',
              safeJsonParse(externalRes, {}),
              undefined,
              { trigger: 'jump' },
            );
          }
        }

        if (isCommonOrTest()) {
          if ([...HELLOICE].includes(companyUUID)) {
            window.addEventListener('cartx/page/change', () => {
              const addCartJumpFlag = getSession(STORAGE_KEY.ADD_CART_JUMP);
              if (addCartJumpFlag === '1') {
                const externalRes = getSession(STORAGE_KEY.CART_LAST_IMAGE);
                if (externalRes) {
                  load(
                    2,
                    'form/out/addPurchaseLoad',
                    safeJsonParse(externalRes, {}),
                    undefined,
                    { trigger: 'jump' },
                  );
                }
              }
            });
          }
        }

        //展示本地存储的banner or floatBanner
        showBanner();

        showIcon(upgradeLoad);

        // 渠道加购弹窗逻辑（构建时由 platformAdapter alias 注入当前平台实现）
        initPlatformAddCart(load);

        if (process.env.NODE_ENV !== 'production') {
          window.addEventListener('message', (e) => {
            if (e.data === 'addCart') {
              load(2, 'form/out/addPurchaseLoad');
            }
          });
        }

        // 只有468 和 test jf站 加载这个埋点
        if (isXikeOrTest()) {
          init468();
        }

        // Firebase Web Push（当前仅 allbuy 灰度）
        if (
          [shopify[30], 'm2bqlnmq', 'mihbz85b', 'misoaaec'].includes(
            companyUUID,
          )
        ) {
          import('@/firebase/index').then((defaultExport) => {
            defaultExport.initFirebase();
          });
        }

        // IM email收集
        if (isXikeOrTest()) {
          // xike无cartq 单独适配
          if (getQueryVariable('cid')) {
            setLocal(STORAGE_KEY.CARTX_CID, getQueryVariable('cid'));
          }
          chatMonitor();
        }

        // checkout收集
        checkoutMonitor();

        if (isEnableNewsletterMonitor()) {
          if (isShopifyOrTest()) {
            shopifyNewsletter();
          } else if (isShoplineOrTest()) {
            shoplineNewsletter();
          }
        }

        const initEmbeddedPopup = () => {
          const popupConfig = safeJsonParse<any>(
            getSession(STORAGE_KEY.POPUP),
            {} as any,
          );
          debug('initEmbeddedPopup popupConfig=%o', popupConfig);

          // 找到嵌入弹窗配置
          if (popupConfig.rets) {
            popupConfig.rets.map((item) => {
              if (item.triggerEventType === 7) {
                const domId = `cartx-form-${item.id}`;
                const rootNode = document.getElementById(domId);
                if (rootNode) {
                  if ([common[1384]].includes(companyUUID)) {
                    if (rootNode.dataset.rendered === 'true') {
                      return 'rendered';
                    }
                    rootNode.dataset.rendered = 'true';
                  }
                  const root = ReactDOM.createRoot(rootNode);
                  if (item.settings?.rules?.endCloseColor) {
                    root.render(<EmbeddedPopupNew popupConfig={item} />);
                  } else {
                    root.render(<EmbeddedPopup popupConfig={item} />);
                  }
                }
              }
              return item;
            });
          }
        };
        // 更新弹窗配置
        checkConfigFrom(7)
          .then((res) => {
            debug('initEmbeddedPopup checkConfigFrom res=%o', res);
            if ([common[1384]].includes(companyUUID)) {
              initEmbeddedPopup();
            }
          })
          .catch((e) => {
            console.log(e);
          });
        initEmbeddedPopup();
        window.addEventListener(EVENTS.POPUP.STORED, initEmbeddedPopup);

        // 清除icon缓存
        const initClearIconCache = () => {
          const popupConfig = safeJsonParse<any>(
            getSession(STORAGE_KEY.POPUP),
            { rets: [] } as any,
          );
          const triggerPop = safeJsonParse<any>(
            getLocal(STORAGE_KEY.TEMP_POP),
            {} as any,
          );
          const findActivePop = popupConfig.rets.find(
            (item) => item.id === triggerPop.id,
          );
          debug(
            'icon cache popupConfig=%o triggerPop=%o findActivePop=%o',
            popupConfig,
            triggerPop,
            !!findActivePop,
          );

          if (!findActivePop) {
            debug('icon cache clear');
            removeLocal(STORAGE_KEY.TEMP_POP);
          }
        };
        if ('m0gcj4cz' === companyUUID) {
          initClearIconCache();
        }
        window.addEventListener(EVENTS.POPUP.STORED, initClearIconCache);
      }
    };

    // 乐其兼容单页面
    if (
      (isCommonOrTest() &&
        ([...SMALLRIG].includes(companyUUID) ||
          window.CARTSEE_SUBSCRIBE?.config?.spa === true)) ||
      (isSimpleOrTest() && window.CARTSEE_SUBSCRIBE?.config?.spa === true) ||
      (isShopifyOrTest() && window.CARTSEE_SUBSCRIBE?.config?.spa === true)
    ) {
      // 切换多语言 删除弹窗配置
      const popupConfig = safeJsonParse<any>(
        getSession(STORAGE_KEY.POPUP),
        {} as any,
      );
      const companyStaticUrl = getCompanyStaticUrl();
      if (popupConfig.companyStaticUrl !== companyStaticUrl) {
        debug(
          'smallrig remove popupConfig.companyStaticUrl=%s companyStaticUrl=%s',
          popupConfig.companyStaticUrl,
          companyStaticUrl,
        );
        removeSession(STORAGE_KEY.POPUP);
      }
    }

    main();

    // 乐其兼容单页面
    if (
      (isCommonOrTest() &&
        ([...SMALLRIG].includes(companyUUID) ||
          window.CARTSEE_SUBSCRIBE?.config?.spa === true)) ||
      (isSimpleOrTest() && window.CARTSEE_SUBSCRIBE?.config?.spa === true) ||
      (isShopifyOrTest() && window.CARTSEE_SUBSCRIBE?.config?.spa === true)
    ) {
      window.addEventListener(PATH_CHANGE_EVENT, () => {
        // 前置清理待展现的弹窗
        clearAll(true);
        // 清理icon
        clearDom('#cartx-icon');
        // 清理嵌入弹窗
        clearDom('.cartx-embedded-popup');
        // 清理弹窗
        deleteDOM('cartx-wrap');
        deleteDOM('cartx-toleave-wrap');
        deleteDOM('cartx-purchase-wrap');
        deleteDOM('cartx-toleave-wrap');
        deleteDOM('cartx-coupon-wrap');

        // 执行弹窗
        window.cartxPopAPI = false;
        main();
      });
    }
  } // end: non-crawler
} catch (error) {
  console.error(error);
  LogJsError({
    em: error.message,
    es: error.stack,
  });
}
